import type {
  LedgerAccountUniquenessResult,
  ValidateLedgerAccountUniquenessQueryParams,
} from './types';

import {
  type QueryFunctionContext,
  queryOptions,
  skipToken,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';

import { type VestigasHTTPError } from '@vestigas/data';

import { queryKeysLedgerAccount } from '../queryKeys';

import { fetchLedgerAccountUniqueness } from './fetchLedgerAccountUniqueness';

/**
 * Generates the query options for the uniqueness check so they can be shared
 * between the useQuery hook and any imperative `queryClient.fetchQuery` call
 * (e.g. validating on blur before a form submit).
 *
 * @param queryParams - The number/company/valid-from combination to check, or
 * `undefined` while the form doesn't have all three yet.
 * @param options - Additional options for the query.
 * @returns The query options including queryKey, queryFn, and other settings.
 */
export const getLedgerAccountUniquenessQueryOptions = <
  TData = LedgerAccountUniquenessResult,
>({
  queryParams,
  options,
}: {
  queryParams: ValidateLedgerAccountUniquenessQueryParams | undefined;
  options?: Omit<
    UseQueryOptions<LedgerAccountUniquenessResult, VestigasHTTPError, TData>,
    'queryKey' | 'queryFn'
  >;
}) =>
  queryOptions({
    queryFn: queryParams
      ? async ({ signal }: QueryFunctionContext) =>
          fetchLedgerAccountUniqueness(queryParams, { signal })
      : skipToken,
    queryKey: queryKeysLedgerAccount.validateUniqueness(
      queryParams ?? { companyId: '', number: '', validFrom: '' },
    ),
    staleTime: 0, // Always fetch fresh data
    ...options,
  });

/**
 * React Query based custom hook for checking whether a ledger account
 * number/company/valid-from combination is already taken.
 *
 * @param queryParams - The combination to check, or `undefined` to skip the
 * query (e.g. while the form is incomplete).
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 * @see https://app.dev.vestigas.com/redoc#tag/Ledger-Accounts/operation/validate_ledger_account_uniqueness_ledger_accounts_validate_uniqueness_get
 */
// fallow-ignore-next-line unused-export -- intended data-layer API surface; the form validator calls the options factory, not this hook.
export const useQueryValidateLedgerAccountUniqueness = <
  TData = LedgerAccountUniquenessResult,
>(
  queryParams: ValidateLedgerAccountUniquenessQueryParams | undefined,
  options?: Omit<
    UseQueryOptions<LedgerAccountUniquenessResult, VestigasHTTPError, TData>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery(
    getLedgerAccountUniquenessQueryOptions<TData>({ options, queryParams }),
  );
